<template>
  <transition name="kf-fade">
    <a
      v-if="show"
      class="km-support-fab"
      :class="{ 'km-support-fab--locked': !isAuthed, 'km-support-fab--left': fabOnLeft }"
      href="#"
      @click.prevent="onClick"
      :aria-label="isAuthed ? 'Live support' : 'Login to chat with support'"
      :title="isAuthed ? 'Live support' : 'Login to chat with support'"
    >
      <span class="km-support-fab__ping"></span>
      <span class="km-support-fab__ping km-support-fab__ping--delay"></span>
      <span class="km-support-fab__icon">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8Z"/>
        </svg>
      </span>
      <span v-if="isAuthed" class="km-support-fab__dot" aria-hidden="true"></span>
      <span v-else class="km-support-fab__lock" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
          <rect x="4" y="10" width="16" height="11" rx="2"/>
          <path d="M8 10V7a4 4 0 0 1 8 0v3"/>
        </svg>
      </span>
    </a>
  </transition>
</template>

<script>
export default {
  name: 'SupportFab',
  computed: {
    isAuthed(){
      return !!localStorage.getItem('token');
    },
    show(){
      const n = this.$route && this.$route.name;
      const HIDE = ['login','register','login-legacy','register-legacy',
                    'support','ServiceOnline','ServicePage','chat-thread','Deposit'];
      return HIDE.indexOf(n) === -1;
    },
    /** Pages with a fixed bottom action bar — move FAB to the left so it never covers Submit. */
    fabOnLeft(){
      const n = this.$route && this.$route.name;
      const LEFT = ['Lottery', 'Deposit'];
      return LEFT.indexOf(n) !== -1;
    }
  },
  methods: {
    onClick(){
      if(!this.isAuthed){
        // Nudge to login, then return to support after auth.
        try {
          if(this.$toast) this.$toast('Please log in to chat with support');
        } catch(e) { /* toast optional */ }
        this.$router.push({ path: '/Login', query: { redirect: '/Support' } });
        return;
      }
      if(this.$route.path !== '/Support'){
        this.$router.push('/Support');
      }
    }
  }
};
</script>

<style scoped>
.km-support-fab {
  position: fixed;
  right: 18px;
  bottom: calc(100px + env(safe-area-inset-bottom, 0));
  z-index: 45;
  width: 52px; height: 52px; border-radius: 50%;
  background: linear-gradient(135deg, #ff4d8d 0%, #a020f0 100%);
  display: flex; align-items: center; justify-content: center;
  color: #fff; text-decoration: none;
  box-shadow:
    0 14px 30px rgba(255,77,141,.45),
    0 0 0 3px rgba(255,255,255,.9),
    inset 0 -2px 6px rgba(0,0,0,.18);
  transition: transform .2s ease, box-shadow .25s ease;
  -webkit-tap-highlight-color: transparent;
}
.km-support-fab:hover {
  transform: translateY(-2px) scale(1.03);
  box-shadow:
    0 18px 36px rgba(255,77,141,.55),
    0 0 0 3px rgba(255,255,255,.9),
    0 0 0 7px rgba(255,77,141,.22),
    inset 0 -2px 6px rgba(0,0,0,.2);
}
.km-support-fab:active { transform: scale(.96); }

/* Certify / lottery: keep clear of the Confirm button */
.km-support-fab--left {
  left: 18px;
  right: auto;
  bottom: calc(148px + env(safe-area-inset-bottom, 0));
}
@media (max-width: 360px) {
  .km-support-fab--left { left: 14px; bottom: calc(138px + env(safe-area-inset-bottom, 0)); }
}

/* Subtle pulsing rings to draw attention */
.km-support-fab__ping {
  position: absolute; inset: -4px; border-radius: 50%;
  border: 2px solid rgba(255,77,141,.55);
  animation: kfPing 2.4s cubic-bezier(.4,0,.2,1) infinite;
  pointer-events: none;
}
.km-support-fab__ping--delay { animation-delay: 1.2s; }
@keyframes kfPing {
  0%   { transform: scale(.95); opacity: .85; }
  80%  { transform: scale(1.7);  opacity: 0; }
  100% { transform: scale(1.7);  opacity: 0; }
}

.km-support-fab__icon { position: relative; z-index: 1; display: flex; }

/* Green "online" dot (shown when logged in) */
.km-support-fab__dot {
  position: absolute; top: 2px; right: 2px; z-index: 2;
  width: 13px; height: 13px; border-radius: 50%;
  background: #10b981;
  border: 2.5px solid #fff;
  box-shadow: 0 2px 6px rgba(16,185,129,.45);
}

/* Lock badge (shown when logged out) */
.km-support-fab__lock {
  position: absolute; top: -2px; right: -2px; z-index: 2;
  width: 18px; height: 18px; border-radius: 50%;
  background: #fff; color: #a020f0;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 2px 6px rgba(160,32,240,.35);
}

/* Locked state tint (still prominent, just visually distinguishable) */
.km-support-fab--locked {
  background: linear-gradient(135deg, #b475c9 0%, #8a52c5 100%);
}
.km-support-fab--locked .km-support-fab__ping { border-color: rgba(160,32,240,.45); }

@media (min-width: 960px) {
  .km-support-fab {
    right: 24px;
    bottom: 24px;  /* bottom bar is hidden on desktop */
    width: 56px; height: 56px;
  }
}
@media (max-width: 360px) {
  .km-support-fab { right: 14px; bottom: calc(90px + env(safe-area-inset-bottom, 0)); width: 46px; height: 46px; }
  .km-support-fab__dot { width: 11px; height: 11px; top: 1px; right: 1px; }
}

/* Entry transition */
.kf-fade-enter-active, .kf-fade-leave-active { transition: opacity .25s ease, transform .25s ease; }
.kf-fade-enter, .kf-fade-leave-to { opacity: 0; transform: scale(.6); }
</style>
